//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles, Grid, Tooltip } from '@material-ui/core';
import SimpleBar from 'simplebar-react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent, logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { setClassNameInDefns } from '../common/definitionHelper';
import { getFormattedDate, getUserDateFormat } from '../common/dateHelper';
import { getUniqueKey } from '../common/arrayHelper';
import {
    DASHBOARD_MY_JOBS,
    BASE_URL,
    DASHBOARD_ID_MY_JOBS,
    DASHBOARD_ID_MY_SETTINGS,
    DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION,
    SORT_OPTION_NEWEST_FIRST,
    SORT_OPTION_OLDEST_FIRST,
    PAGE_TITLE_EMP_MY_JOBS,
    ANALYTICS_EVENT_SEARCH,
    EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED,
    EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_APPLIED,
} from '../common/constants';
import { PATH_DASHBOARD } from '../system/constants';
import {
    ELASTICSEARCH_PAGE_SIZE,
    ELASTICSEARCH_SORT_OPTIONS,
    ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_ASC,
    ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC,
} from '../common/elasticSearchHelper';
import { PLACEHOLDER_SEARCH_BY_JOB_TITLE_JOB_ID_NUMBER } from '../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { fetchEmpJobs } from '../common/services/empMyJobsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
import AfitdButton from '../common/elemental/AfitdButton';
import AfitdButtonGroup from '../common/elemental/AfitdButtonGroup';
import AfitdDropDown from '../common/elemental/AfitdDropDown';
import AfitdPagination from '../common/elemental/AfitdPagination';
import SearchInput from '../dashboardMyJobs/subComponents/SearchInput';
import AfitdConfirmDialog from '../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import JobCard from './subComponents/JobCard';
import AddJobDialog from './subComponents/AddJobDialog';
import HiredDialog from './subComponents/HiredModal';
import SingleJobDialog from './subComponents/SingleJobDialog';
import Footer from '../dashboard/subComponents/Footer';
import noJobFound from '../images/NoJobFound/noJobFound.png';
import JobSeekersDialog from './subComponents/JobSeekersDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import _remove from 'lodash/remove';
import _slice from 'lodash/slice';
import _findIndex from 'lodash/findIndex';
import _filter from 'lodash/filter';
import _some from 'lodash/some';
import _omit from 'lodash/omit';
import _forEach from 'lodash/forEach';
import _orderBy from 'lodash/orderBy';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _size from 'lodash/size';

import { differenceInSeconds } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        height: '100%',
        background: '#FAFAFC',
    },
    header: {
        height: '128px',
    },
    content: {
        flex: 1,
        overflowY: 'hidden',
        marginTop: 0,
        marginBottom: 0,
        borderRadius: '0 0 10px 10px',
        backgroundColor: '#fff',
    },
    contentScrollable: {
        width: '100%',
        height: '100%',
        overflowY: 'hidden',
        // overflowY: 'auto',
        // '&::-webkit-scrollbar-track': { borderRadius: '10px', background: 'none' },
        // '&::-webkit-scrollbar': { width: '8px', backgroundColor: '#F5F5F5' },
        // '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.2)' },
    },
    myJobs: {
        padding: '0px 50px 40px 50px',
        [theme.breakpoints.down('sm')]: { padding: '16px', paddingTop: 0 },
    },
    myJobsSearchContainer: {
        backgroundColor: '#fff',
        padding: '40px 50px 0px 50px',
        borderRadius: '10px 10px 0 0',
        [theme.breakpoints.down('sm')]: { padding: '16px', paddingBottom: 0 },
    },
    dashboard: { background: '#FAFAFC' },
    mainContainer: { marginTop: 40, borderRadius: 10, backgroundColor: '#fff' },
    dashboardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    title: { fontSize: '26px', color: '#111E14', fontWeight: '500', padding: '40px 0 10px 0', [theme.breakpoints.down('sm')]: { fontSize: '20px' } },
    dateBox: { display: 'flex', alignItems: 'center' },
    calendarIcon: { color: 'rgba(17, 30, 20, 0.6)', fontSize: 16 },
    todayContainer: {
        marginTop: '1px',
        marginLeft: '9px',
        fontSize: '13px',
        color: 'rgba(17, 30, 20, 0.6)',
    },
    helpIcon: { fontSize: 22 },
    date: { color: 'rgba(17, 30, 20, 0.6)', marginLeft: '5px' },
    searchContainer: { margin: '24px 0', display: 'flex', alignItems: 'center' },
    jobsFoundText: {
        fontWeight: 'bold',
        [theme.breakpoints.down('sm')]: {
            fontSize: '18px',
        },
    },
    sortBox: { display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' },
    sortText: { padding: '0 8px' },
    // innerContentGrid: { padding: '40px 50px' }, // renamed to myJobs
    addIcon: {
        // fontSize: 30,
        fontSize: 45,
        padding: 10,
        borderRadius: 50,
        backgroundColor: 'rgba(0,0,0,0.03)',
        margin: '40px 0 0 30px',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: { margin: '8px 0 0 8px' },
    },
    addJobContainer: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
    helpIconContainer: {
        marginRight: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        borderRadius: 50,
        height: 45,
        width: 45,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    // contentGrid: {
    //     overflowY: 'scroll',
    //     height: '70vh',
    //     '&::-webkit-scrollbar-track': { borderRadius: '10px', background: 'none' },
    //     '&::-webkit-scrollbar': { width: '8px', backgroundColor: '#F5F5F5' },
    //     '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.2)' },
    // }, // renamed to contentScrollable
    dropdownContainer: { width: 200, [theme.breakpoints.down('sm')]: { width: 145 } },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 10,
    },
    noJobFoundContainer: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
    noJobFoundImg: { width: 450, [theme.breakpoints.down('sm')]: { width: 340 } },
    noJobListingText: { fontSize: 20, fontWeight: 'bold' },
    noJobListingPara: { fontSize: 16, color: '#6F6D7A' },
    noJobTextContainer: { textAlign: 'center' },
    activeJobsButton: {
        [theme.breakpoints.down('sm')]: {
            fontSize: '12px',
            padding: '6px 12px',
        },
    },
    inactiveJobsButton: {
        [theme.breakpoints.down('sm')]: {
            fontSize: '12px',
            padding: '6px 12px',
        },
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        userDateFormat,
        profileData,
        setShowAddModal,
        showAddModal,
        jobToEdit,
        editJobModalOpen,
        handleCloseEditJobModal,
        hiredModalOpen,
        handleCloseHiredModal,
        isActiveView,
        // setIsActiveView,
        // setSortByNewest,
        sortByNewest,
        limitExceeded,
        jobList,
        jobModalIdx,
        setJobModalIdx,
        handleRunSearch,
        handleClickActiveButton,
        handleClickInactiveButton,
        handleChangeSort,
        onConfirmActiveJobLimitDialog,
        openActiveJobLimitDialog,
        setOpenActiveJobLimitDialog,
        handleClearFilters,
        handleAddJob,
        handleUpdateJob,
        handleSaveHiredCandidates,
        selectedJobForListModal,
        setSelectedJobForListModal,
        unreadNewApplicantsForListModal,
        listModalOpen,
        isSavedList,
        // setIsSavedList,
        selectedJobAppliedJobSeekers,
        selectedJobSavedJobSeekers,
    } = params;

    const sortOptions = [SORT_OPTION_NEWEST_FIRST, SORT_OPTION_OLDEST_FIRST];

    const defns = {
        root: { item: true, container: true, direction: 'column', lg: 12, md: 12, sm: 12, xs: 12 },
        content: { container: true, item: true, spacing: 0 },

        dashboard: { maxWidth: false },
        containerGrid: { container: true, spacing: 2 },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },

        // CONTENT
        // contentGrid: { item: true, lg: 12, md: 12, sm: 12, xs: 12 }, // replaced with contentScrollable DIV
        sortText: { variant: 'body2', color: 'secondary' },
        jobsFoundText: { variant: 'h6' },
        sortDropdown: { options: sortOptions, value: sortByNewest ? SORT_OPTION_NEWEST_FIRST : SORT_OPTION_OLDEST_FIRST, handleChange: e => handleChangeSort(e.target.value) },
        activeJobsButton: { color: isActiveView ? 'secondary' : '', onClick: handleClickActiveButton },
        inactiveJobsButton: { color: !isActiveView ? 'secondary' : '', onClick: handleClickInactiveButton },
        addIcon: { onClick: () => (!limitExceeded ? setShowAddModal(true) : setOpenActiveJobLimitDialog(true)) },
        addJobModal: {
            show: showAddModal,
            close: () => setShowAddModal(false),
            limitExceeded,
            profileData,
            onJobAdd: handleAddJob,
            // onFunny: () => {
            //     alert('funny');
            // },
        },
        editJobModal: {
            show: editJobModalOpen,
            close: handleCloseEditJobModal,
            job: jobToEdit,
            profileData,
            onJobUpdate: handleUpdateJob,
        },
        hiredModal: { open: hiredModalOpen, close: handleCloseHiredModal, job: jobToEdit, onHiredCandidatesSave: handleSaveHiredCandidates },
        tooltip: { title: 'Create new job requisition' },
        jobModal: { open: jobModalIdx !== null, close: () => setJobModalIdx(null), currentJobs: jobList, idx: jobModalIdx },

        searchInput: {
            placeholder: PLACEHOLDER_SEARCH_BY_JOB_TITLE_JOB_ID_NUMBER,
            onSearch: handleRunSearch,
            showClearFilters: true,
            onClearFilters: handleClearFilters,
        },
        jobLimitExceedDialog: {
            title: 'Interaction limit exceeded!',
            message: "Your limit of simultaneous 'Active' jobs has been reached. Please upgrade your subscription to post more job listings.",
            yesButtonLabel: 'Upgrade',
            yesButtonColor: 'secondary',
            noButtonLabel: 'Cancel',
            onConfirm: onConfirmActiveJobLimitDialog,
            open: openActiveJobLimitDialog,
            onClose: () => setOpenActiveJobLimitDialog(false),
        },
        listModal: {
            open: listModalOpen,
            onClose: () => setSelectedJobForListModal(null), //setListModalOpen(false),
            title: `Job seekers ${isSavedList ? 'saved' : 'that have applied'} to this job`,
            isSavedList,
            listType: isSavedList ? EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED : EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_APPLIED,
            limitExceeded,
            jobSeekers: isSavedList ? selectedJobSavedJobSeekers : selectedJobAppliedJobSeekers,
            job: selectedJobForListModal,
            unreadNewApplicants: unreadNewApplicantsForListModal,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const queryInitState = {
    searchText: null,
    sortTag: ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC],
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MyJobs = () => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const theme = useTheme();
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();

    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const { employerId, currentEmployer, sys, esToken, setEsToken } = appContext;
    const { dashboardData, profileData, setIsRequestLoadProfile, setAsyncCallCount } = context;

    const userDateFormat = getUserDateFormat(appContext);
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const myRef = useRef(null);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [query, setQuery] = useState(queryInitState);
    const [sortTag, setSortTag] = useState();
    const [searchResultTotal, setSearchResultTotal] = useState(0);
    const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editJobModalOpen, setEditJobModalOpen] = useState(false);
    const [jobToEdit, setJobToEdit] = useState();
    const [hiredModalOpen, setHiredModalOpen] = useState(false);
    const [isActiveView, setIsActiveView] = useState(true);
    const [jobList, setJobList] = useState([]);
    const [activeJobsCount, setActiveJobsCount] = useState(0);
    const [inactiveJobsCount, setInactiveJobsCount] = useState(0);
    const [sortByNewest, setSortByNewest] = useState(SORT_OPTION_NEWEST_FIRST);
    const [openActiveJobLimitDialog, setOpenActiveJobLimitDialog] = useState(false);
    const [jobModalIdx, setJobModalIdx] = useState(null);
    const [isSearched, setIsSearched] = React.useState(false);
    const [isRequestSearch, setIsRequestSearch] = React.useState(false);
    const [selectedJobForListModal, setSelectedJobForListModal] = React.useState(null);
    const [isSavedList, setIsSavedList] = useState(false);

    const [tempUpdatedJobs, setTempUpdatedJobs] = React.useState({});
    const [tempDeletedJobs, setTempDeletedJobs] = React.useState({});

    const lastQuery = useRef();
    const lastQueryPageNumber = useRef();
    const lastIsActiveView = useRef();

    const paginationNumber = Math.ceil(searchResultTotal / ELASTICSEARCH_PAGE_SIZE);
    const handleChangePage = (event, value) => {
        setCurrentPageNumber(value);
    };

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const tiers = _get(sys, 'tiers');
    const activeTier = _get(currentEmployer, 'tier');
    const userTier = tiers?.find(t => t.tier === activeTier);
    const activeJobMax = _get(userTier, 'activeJobMax');
    const limitExceeded = activeJobsCount >= activeJobMax;
    const searchTextInQuery = _get(query, 'searchText');
    const listModalOpen = !_isNil(selectedJobForListModal);
    const selectedJobAppliedJobSeekers = _map(selectedJobForListModal?.applicants, jobSeekerId => ({
        ..._get(selectedJobForListModal?.jobSeekers, jobSeekerId),
        userId: jobSeekerId,
    }));
    const selectedJobSavedJobSeekers = _map(selectedJobForListModal?.savedJs, jobSeekerId => ({ ..._get(selectedJobForListModal?.jobSeekers, jobSeekerId), userId: jobSeekerId }));

    const unreadNewApplicantsAll = _get(dashboardData, 'unreadNewApplicants');
    const unreadNewApplicantsForListModal = _get(unreadNewApplicantsAll, selectedJobForListModal?.uid, []);

    // console.log({ activeJobMax, limitExceeded });

    //---------------------------------------------------------------------
    // ElasticSearch
    //---------------------------------------------------------------------
    const runSearch = useCallback(
        async ({ page = 1, isActiveView } = {}) => {
            const queryWithUserId = { ...query, parentId: employerId };

            setAsyncCallCount && setAsyncCallCount(prev => prev + 1);
            const searchResult = await fetchEmpJobs({
                queryObject: queryWithUserId,
                from: page,
                activeView: isActiveView,
                esToken,
                setEsToken,
            });

            const activeJobsResult = _get(searchResult, 'activeJobs');
            let activeCount = _get(activeJobsResult, 'total', 0);
            let activeJobList = _get(activeJobsResult, 'list', []);

            const inactiveJobsResult = _get(searchResult, 'inactiveJobs');
            let inactiveCount = _get(inactiveJobsResult, 'total', 0);
            let inactiveJobList = _get(inactiveJobsResult, 'list', []);

            // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> activeCount / inactiveCount', activeCount, inactiveCount);

            // illusion of real-time for new/updated jobs
            const updatedJobKeys = _keys(tempUpdatedJobs);
            const updatedJobList = _orderBy(
                _map(updatedJobKeys, jobId => tempUpdatedJobs[jobId]),
                ['updateDt'],
                ['asc'],
            );
            _forEach(updatedJobList, updatedJob => {
                const data = updatedJob.data || {};
                // we only care about the ones that was updated within the last 30 seconds
                if (differenceInSeconds(new Date(), updatedJob.updateDt) < 30) {
                    if (updatedJob.isNew) {
                        // if new job and page 1 and newest first but new job not in result, prepend it
                        if (page === 1 && query?.sortTag === ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC]) {
                            if (data.active) {
                                if (!_some(activeJobList, item => item.uid === data.uid)) {
                                    // add the new job to the active list
                                    activeJobList = [data, ...activeJobList];
                                    activeCount++;
                                    // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> active new activeCount++');
                                }
                            } else {
                                if (!_some(inactiveJobList, item => item.uid === data.uid)) {
                                    // add the new job to the inactive list
                                    inactiveJobList = [data, ...inactiveJobList];
                                    inactiveCount++;
                                    // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> inactive new inactiveCount++');
                                }
                            }
                        }
                    } else {
                        // if the updated job is in the result, replace the one in the result with it
                        const foundActiveJobIdx = _findIndex(activeJobList, item => item.uid === data.uid);
                        const foundInactiveJobIdx = _findIndex(inactiveJobList, item => item.uid === data.uid);
                        // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> foundActiveJobIdx / foundInactiveJobIdx', foundActiveJobIdx, foundInactiveJobIdx);
                        if (data.active) {
                            // updated job is active
                            if (foundActiveJobIdx >= 0) {
                                // update it in the active job list
                                activeJobList = [data, ..._slice(activeJobList, 0, foundActiveJobIdx), ..._slice(activeJobList, foundActiveJobIdx + 1)];
                            } else {
                                // prepend it in the active job list
                                if (page === 1 && query.sortTag === ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC]) {
                                    activeJobList = [data, ...activeJobList];
                                    activeCount++;
                                    // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> active !foundActiveJobIdx activeCount++');
                                }
                            }
                            if (foundInactiveJobIdx >= 0) {
                                // remove it from the inactive job list
                                inactiveJobList = [..._slice(inactiveJobList, 0, foundInactiveJobIdx), ..._slice(inactiveJobList, foundInactiveJobIdx + 1)];
                                inactiveCount--;
                                // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> active foundInactiveJobIdx inactiveCount--');
                            }
                        } else {
                            // updated job is inactive
                            if (foundInactiveJobIdx >= 0) {
                                // update it in the inactive job list
                                inactiveJobList = [data, ..._slice(inactiveJobList, 0, foundInactiveJobIdx), ..._slice(inactiveJobList, foundInactiveJobIdx + 1)];
                            } else {
                                // prepend it in the inactive job list
                                if (page === 1 && query.sortTag === ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC]) {
                                    inactiveJobList = [data, ...inactiveJobList];
                                    inactiveCount++;
                                    // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> inactive !foundInactiveJobIdx inactiveCount++');
                                }
                            }
                            if (foundActiveJobIdx >= 0) {
                                // remove it from the active job list
                                activeJobList = [..._slice(activeJobList, 0, foundActiveJobIdx), ..._slice(activeJobList, foundActiveJobIdx + 1)];
                                activeCount--;
                                // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> inactive foundActiveJobIdx activeCount--');
                            }
                        }
                    }
                }
            });

            // illusion of real-time for deleted jobs
            const tempDeletedKeys = _keys(tempDeletedJobs);
            const tempDeletedJobList = _orderBy(
                _map(tempDeletedKeys, jobId => tempDeletedJobs[jobId]),
                ['deleteDt'],
                ['asc'],
            );
            _forEach(tempDeletedJobList, deletedJob => {
                const uid = deletedJob.id;
                // we only care about the ones that was deleted within the last 30 seconds
                if (differenceInSeconds(new Date(), deletedJob.deleteDt) < 30) {
                    // if the deleted job is in the result, remove it
                    const foundActiveJobIdx = _findIndex(activeJobList, item => item.uid === uid);
                    const foundInactiveJobIdx = _findIndex(inactiveJobList, item => item.uid === uid);

                    if (foundActiveJobIdx >= 0) {
                        // remove it from the active job list
                        activeJobList = [..._slice(activeJobList, 0, foundActiveJobIdx), ..._slice(activeJobList, foundActiveJobIdx + 1)];
                        activeCount--;
                        // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> delete foundActiveJobIdx activeCount--');
                    }

                    if (foundInactiveJobIdx >= 0) {
                        // remove it from the inactive job list
                        inactiveJobList = [..._slice(inactiveJobList, 0, foundInactiveJobIdx), ..._slice(inactiveJobList, foundInactiveJobIdx + 1)];
                        inactiveCount--;
                        // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> delete foundInactiveJobIdx inactiveCount--');
                    }
                }
            });

            // do not update the other tab if page is not 1 because elastic might return 0 total if no records in that page (glitch)
            if (page === 1 || isActiveView) setActiveJobsCount(activeCount);
            if (page === 1 || !isActiveView) setInactiveJobsCount(inactiveCount);
            // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>> FINAL activeCount / inactiveCount', activeCount, inactiveCount);

            setSearchResultTotal(isActiveView ? activeCount : inactiveCount);
            setJobList(isActiveView ? activeJobList : inactiveJobList);

            query?.searchText && logEvent(ANALYTICS_EVENT_SEARCH, { search_term: query.searchText, page_title: PAGE_TITLE_EMP_MY_JOBS });

            // myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

            setAsyncCallCount && setTimeout(() => setAsyncCallCount(prev => prev - 1), 500);
        },
        [query, employerId, esToken, setEsToken, setAsyncCallCount, tempUpdatedJobs, tempDeletedJobs],
    );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_EMP_MY_JOBS);
    }, []);

    // run search when query / current page number / active/inactive view has changed
    useEffect(() => {
        if (query && currentPageNumber) {
            const sameQuery = _isEqual(lastQuery.current, query);
            const samePageNumber = lastQueryPageNumber.current === currentPageNumber;
            const sameView = lastIsActiveView.current === isActiveView;

            // if query is same as last, do not search
            if ((lastQuery && lastQueryPageNumber && (!sameQuery || !samePageNumber || !sameView)) || isRequestSearch) {
                runSearch({ page: currentPageNumber, isActiveView });

                // remember the query and the page number and view mode
                lastQuery.current = _cloneDeep(query);
                lastQueryPageNumber.current = currentPageNumber;
                lastIsActiveView.current = isActiveView;

                setIsRequestSearch(false);
            }
        }
    }, [query, currentPageNumber, isActiveView, runSearch, lastQuery, lastQueryPageNumber, lastIsActiveView, isRequestSearch]);

    useEffect(() => {
        if (!_isNil(profileData) && activeJobsCount === 0 && location.pathname === `${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_JOBS}/addJob`) setShowAddModal(true);
    }, [location.pathname, profileData, activeJobsCount]);

    useEffect(() => {
        // on page load, fetch the profile data (if not already done)
        if (_isNil(profileData)) {
            setIsRequestLoadProfile(true);
        }
    }, [profileData, setIsRequestLoadProfile]);

    //---------------------------------------------------------------------
    // useCallback / event handlers
    //---------------------------------------------------------------------
    const handleClickJobTitle = useCallback(jobIdx => {
        setJobModalIdx(jobIdx);
    }, []);

    const handleRunSearch = useCallback(
        searchText => {
            // if searching with new keywords, reset current page number to page 1
            if (searchText !== searchTextInQuery) setCurrentPageNumber(1);
            setQuery(prev => ({ ...prev, searchText, sortTag: ELASTICSEARCH_SORT_OPTIONS[sortTag] }));
            setIsSearched(true);
        },
        [searchTextInQuery, sortTag],
    );

    const handleClickActiveButton = useCallback(() => {
        if (!isActiveView) setCurrentPageNumber(1);
        setIsActiveView(true);
        setJobList([]);
        setSearchResultTotal(activeJobsCount);
    }, [isActiveView, activeJobsCount]);

    const handleClickInactiveButton = useCallback(() => {
        if (isActiveView) setCurrentPageNumber(1);
        setIsActiveView(false);
        setJobList([]);
        setSearchResultTotal(inactiveJobsCount);
    }, [isActiveView, inactiveJobsCount]);

    const handleChangeSort = sortOrder => {
        setSortByNewest(sortOrder === SORT_OPTION_NEWEST_FIRST);
        const newSortTag = sortOrder === SORT_OPTION_NEWEST_FIRST ? ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC : ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_ASC;
        setSortTag(newSortTag);
        setQuery(prev => ({ ...prev, sortTag: ELASTICSEARCH_SORT_OPTIONS[newSortTag] }));
    };

    const onConfirmActiveJobLimitDialog = () => {
        setOpenActiveJobLimitDialog(false);
        history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION}`);
    };

    const handleClearFilters = () => {
        setQuery(queryInitState);
        setSortByNewest(SORT_OPTION_NEWEST_FIRST);
        setIsSearched(false);
    };

    const deleteJobFromScreen = (jobId, isChangeStatus, newIsActive) => {
        // remove the job from the screen because ElasticSearch doesn't have real-time update and will not auto refresh
        setJobList(prev => {
            const newJobList = [...prev];
            _remove(newJobList, item => item.uid === jobId);
            return newJobList;
        });

        setSearchResultTotal(prev => prev - 1);

        // update the counts on the screen because ElasticSearch doesn't have real-time update and will not auto refresh
        if (isChangeStatus) {
            if (newIsActive) {
                // changing to active
                setActiveJobsCount(prev => prev + 1);
                setInactiveJobsCount(prev => prev - 1);
            } else {
                // changing to inactive
                setActiveJobsCount(prev => prev - 1);
                setInactiveJobsCount(prev => prev + 1);
            }
        } else {
            if (isActiveView) {
                setActiveJobsCount(prev => prev - 1);
            } else {
                setInactiveJobsCount(prev => prev - 1);
            }
        }
    };

    const handleAddJob = newEmpJob => {
        // // Update the job data in the 'jobList' state instead of relying ElasticSearch to return the latest data because it can take up to 30 seconds -el 20220415
        // const newIsActive = newEmpJob?.active;

        // // display job and update the count on screen if the status matches the view
        // if ((newIsActive && isActiveView) || (!newIsActive && !isActiveView)) {
        //     setJobList(prev => [newEmpJob, ...prev]);
        //     setSearchResultTotal(prev => prev + 1);
        // }

        // // update the status counts on the screen
        // if (newIsActive) setActiveJobsCount(prev => prev + 1);
        // else setInactiveJobsCount(prev => prev + 1);

        // remember the job for illusion of real-time
        setTempUpdatedJobs(prev => {
            let cleanedUpList = { ...prev };
            _forEach(_keys(prev), jobId => {
                if (differenceInSeconds(new Date(), prev[jobId].updateDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
            });
            return { ...cleanedUpList, [newEmpJob.uid]: { data: newEmpJob, isNew: true, updateDt: new Date() } };
        });

        // reset the search to default and active view
        setJobList([]);
        setIsActiveView(newEmpJob.active);
        setCurrentPageNumber(1);
        setQuery(queryInitState);
        setSortByNewest(SORT_OPTION_NEWEST_FIRST);
        setIsRequestSearch(true);
    };

    const handleClickJobMenuEdit = selectedJob => {
        setJobToEdit(selectedJob);
        setEditJobModalOpen(true);
    };

    const handleCloseEditJobModal = () => {
        setEditJobModalOpen(false);
        setJobToEdit(null);
    };

    const handleClickJobMenuHiredCandidates = selectedJob => {
        setJobToEdit(selectedJob);
        setHiredModalOpen(true);
    };

    const handleCloseHiredModal = () => {
        setHiredModalOpen(false);
        setJobToEdit(null);
    };

    const handleUpdateJob = updatedEmpJob => {
        // // Update the job data in the 'jobList' state instead of relying ElasticSearch to return the latest data because it can take up to 30 seconds -el 20220415
        // const jobId = updatedEmpJob?.uid;
        // const newIsActive = updatedEmpJob?.active;

        // if ((newIsActive && isActiveView) || (!newIsActive && !isActiveView)) {
        //     setJobList(prev => {
        //         const currentJobIdx = _findIndex(prev, item => item.uid === jobId);
        //         return [..._slice(prev, 0, currentJobIdx), updatedEmpJob, ..._slice(prev, currentJobIdx + 1)];
        //     });
        // } else {
        //     // job is not in view anymore, remove it and adjust the counts on the screen because ElasticSearch doesn't have real-time update and will not auto refresh
        //     deleteJobFromScreen(jobId, newIsActive);
        // }

        // remember the job for illusion of real-time
        setTempUpdatedJobs(prev => {
            let cleanedUpList = { ...prev };
            _forEach(_keys(prev), jobId => {
                if (differenceInSeconds(new Date(), prev[jobId].updateDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
            });
            return { ...cleanedUpList, [updatedEmpJob.uid]: { data: updatedEmpJob, isNew: false, updateDt: new Date() } };
        });

        // reset the search to 'Page 1 Newest First'
        setJobList([]);
        setIsActiveView(updatedEmpJob.active);
        setCurrentPageNumber(1);
        setQuery(queryInitState);
        setSortByNewest(SORT_OPTION_NEWEST_FIRST);
        setIsRequestSearch(true);
    };

    const handleDeleteJob = (jobId, newIsActive) => {
        deleteJobFromScreen(jobId, false);

        // remember the job Id for illusion of real-time
        setTempDeletedJobs(prev => {
            let cleanedUpList = { ...prev };
            _forEach(_keys(prev), jobId => {
                if (differenceInSeconds(new Date(), prev[jobId].deleteDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
            });
            return { ...cleanedUpList, [jobId]: { id: jobId, deleteDt: new Date() } };
        });
    };

    const handleChangeJobStatus = (updatedEmpJob = {}, newIsActive) => {
        const jobId = updatedEmpJob.uid;
        deleteJobFromScreen(jobId, true, newIsActive);

        // remember the job for illusion of real-time
        setTempUpdatedJobs(prev => {
            let cleanedUpList = { ...prev };
            _forEach(_keys(prev), jobId => {
                if (differenceInSeconds(new Date(), prev[jobId].updateDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
            });
            return { ...cleanedUpList, [jobId]: { data: { ...updatedEmpJob, active: newIsActive }, isNew: false, updateDt: new Date() } };
        });
    };

    const handleSaveHiredCandidates = (jobId, updatedHiredCandidates) => {
        // Update the data in the 'jobList' state instead of relying ElasticSearch to return the latest data because it can take up to 30 seconds -el 20220415
        setJobList(prev => {
            const currentJobIdx = _findIndex(prev, item => item.uid === jobId);
            const currentJob = prev[currentJobIdx];
            return [..._slice(prev, 0, currentJobIdx), { ...currentJob, hired: updatedHiredCandidates }, ..._slice(prev, currentJobIdx + 1)];
        });
    };

    const handleDeleteSavedJobSeeker = ({ job, jobSeekerId, jobId } = {}) => {
        const savedJs = job?.savedJs;
        const applicants = job?.applicants;
        const jobSeekers = job?.jobSeekers;

        // remove job seeker id from savedJs
        const updatedSavedJs = _filter(savedJs, item => item !== jobSeekerId);

        // if deleted job seeker is not in applicants, also remove the entry from jobSeekers array
        let updatedJobSeekers = jobSeekers;
        const isJobSeekerInApplicants = _some(applicants, item => item === jobSeekerId);
        if (!isJobSeekerInApplicants) {
            updatedJobSeekers = _omit(jobSeekers, [jobSeekerId]);
        }

        // remove the job seeker from the job data
        const updatedJob = { ...job, savedJs: updatedSavedJs, jobSeekers: updatedJobSeekers };

        // update the selectedJobForListModal state
        if (_size(updatedSavedJs) > 0) {
            setSelectedJobForListModal(updatedJob);
        } else {
            setSelectedJobForListModal(null);
        }

        // update the jobList state
        setJobList(prev => {
            const jobIdx = _findIndex(prev, item => item.uid === jobId);
            return [..._slice(prev, 0, jobIdx), updatedJob, ..._slice(prev, jobIdx + 1)];
        });
    };

    const handleDeleteApplicant = ({ job, jobSeekerId, jobId } = {}) => {
        const savedJs = job?.savedJs;
        const applicants = job?.applicants;
        const jobSeekers = job?.jobSeekers;

        // remove job seeker id from applicants
        const updatedApplicants = _filter(applicants, item => item !== jobSeekerId);
        // if deleted job seeker is not in savedJs, also remove the entry from jobSeekers array
        let updatedJobSeekers = jobSeekers;
        const isJobSeekerInSavedJs = _some(savedJs, item => item === jobSeekerId);
        if (!isJobSeekerInSavedJs) {
            updatedJobSeekers = _omit(jobSeekers, [jobSeekerId]);
        }
        // remove the job seeker from the job data
        const updatedJob = { ...job, applicants: updatedApplicants, jobSeekers: updatedJobSeekers };

        // update the selectedJobForListModal state
        if (_size(updatedApplicants) > 0) {
            setSelectedJobForListModal(updatedJob);
        } else {
            setSelectedJobForListModal(null);
        }

        // update the jobList state
        setJobList(prev => {
            const jobIdx = _findIndex(prev, item => item.uid === jobId);
            return [..._slice(prev, 0, jobIdx), updatedJob, ..._slice(prev, jobIdx + 1)];
        });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        userDateFormat,
        profileData,
        setShowAddModal,
        showAddModal,
        jobToEdit,
        editJobModalOpen,
        handleCloseEditJobModal,
        hiredModalOpen,
        handleCloseHiredModal,
        isActiveView,
        setIsActiveView,
        setSortByNewest,
        sortByNewest,
        limitExceeded,
        jobList,
        jobModalIdx,
        setJobModalIdx,
        handleRunSearch,
        handleClickActiveButton,
        handleClickInactiveButton,
        handleChangeSort,
        onConfirmActiveJobLimitDialog,
        openActiveJobLimitDialog,
        setOpenActiveJobLimitDialog,
        handleClearFilters,
        handleAddJob,
        handleUpdateJob,
        handleSaveHiredCandidates,
        selectedJobForListModal,
        setSelectedJobForListModal,
        unreadNewApplicantsForListModal,
        listModalOpen,
        isSavedList,
        setIsSavedList,
        selectedJobAppliedJobSeekers,
        selectedJobSavedJobSeekers,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                isActiveView,
                setJobList,
                setSearchResultTotal,
                setActiveJobsCount,
                setInactiveJobsCount,
                setIsSavedList,
                setSelectedJobForListModal,
                query,
                handleDeleteSavedJobSeeker,
                handleDeleteApplicant,
                setTempUpdatedJobs,
            }}
        >
            <AfitdPageHead title={PAGE_TITLE_EMP_MY_JOBS} />
            <Grid {...defns.root}>
                <Grid {...defns.header}>
                    <div {...defns.dashboardHeader}>
                        <div {...defns.addJobContainer}>
                            <div style={/* quick alignment fix */ { marginTop: 1 }}>
                                <AfitdTypography {...defns.title}>{DASHBOARD_MY_JOBS}</AfitdTypography>
                                <div {...defns.dateBox}>
                                    <CalendarTodayIcon {...defns.calendarIcon} />
                                    <div {...defns.todayContainer}>
                                        <AfitdTypography {...defns.todayText} />
                                    </div>
                                </div>
                            </div>
                            <Tooltip {...defns.tooltip}>
                                <AddIcon {...defns.addIcon} />
                            </Tooltip>
                        </div>

                        <div {...defns.dateBox}>
                            {/* <div {...defns.helpIconContainer}>
                                <HelpOutlineOutlined {...defns.helpIcon} />
                            </div> */}
                            <AfitdButtonGroup>
                                <AfitdButton {...defns.activeJobsButton}>{activeJobsCount} ACTIVE</AfitdButton>
                                <AfitdButton {...defns.inactiveJobsButton}>{inactiveJobsCount} INACTIVE</AfitdButton>
                            </AfitdButtonGroup>
                        </div>
                    </div>
                </Grid>
                <div {...defns.myJobsSearchContainer}>
                    {/* <SearchInput search={search} set={setSearch} /> */}
                    <SearchInput {...defns.searchInput} />
                </div>
                <Grid {...defns.content}>
                    <div {...defns.contentScrollable}>
                        <SimpleBar style={{ height: '100%' }}>
                            <div {...defns.myJobs}>
                                {/* <SearchInput search={search} set={setSearch} /> */}
                                {/* <SearchInput {...defns.searchInput} /> */}
                                <div {...defns.searchContainer}>
                                    <AfitdTypography {...defns.jobsFoundText}>
                                        {`${searchResultTotal} ${isActiveView ? 'Active' : 'Inactive'} job${searchResultTotal === 1 ? '' : 's'}`}
                                    </AfitdTypography>
                                    <div {...defns.sortBox}>
                                        <AfitdTypography {...defns.sortText}>Sort:</AfitdTypography>
                                        <div {...defns.dropdownContainer}>
                                            <AfitdDropDown {...defns.sortDropdown} />
                                        </div>
                                    </div>
                                </div>
                                <div ref={myRef} />

                                {jobList?.length === 0 ? (
                                    searchTextInQuery !== '' && isSearched ? (
                                        <div {...defns.noJobFoundContainer}>
                                            <div>
                                                <img src={noJobFound} alt="" {...defns.noJobFoundImg} />
                                            </div>
                                            <div {...defns.noJobTextContainer}>
                                                <p {...defns.noJobListingText}>No Job listings match your search</p>
                                                <p {...defns.noJobListingPara}>Please enter more details or different search criteria</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div {...defns.noJobFoundContainer}>
                                            <div>
                                                <img src={noJobFound} alt="" {...defns.noJobFoundImg} />
                                            </div>
                                            {isActiveView ? (
                                                <div {...defns.noJobTextContainer}>
                                                    <p {...defns.noJobListingText}>NO JOB LISTINGS CREATED YET</p>
                                                    <p {...defns.noJobListingPara}>Use the '+' icon to create a new job requisition</p>
                                                </div>
                                            ) : (
                                                <div {...defns.noJobTextContainer}>
                                                    <p {...defns.noJobListingText}>NO 'INACTIVE' JOBS TO DISPLAY</p>
                                                </div>
                                            )}
                                        </div>
                                    )
                                ) : (
                                    jobList?.map((job, idx) => (
                                        <JobCard
                                            key={getUniqueKey()}
                                            job={job}
                                            limitExceeded={limitExceeded}
                                            idx={idx}
                                            onJobTitleClick={handleClickJobTitle}
                                            onJobDelete={handleDeleteJob}
                                            onJobStatusChange={handleChangeJobStatus}
                                            profileData={profileData}
                                            // onJobUpdate={handleUpdateJob}
                                            // onHiredCandidatesSave={handleSaveHiredCandidates}
                                            onJobMenuEditClick={handleClickJobMenuEdit}
                                            onJobMenuHiredCandidatesClick={handleClickJobMenuHiredCandidates}
                                        />
                                    ))
                                )}
                                {jobModalIdx !== null && <SingleJobDialog {...defns.jobModal} />}
                            </div>
                            {jobList?.length === 0 ? null : (
                                <div
                                    {...defns.paginationContainer}
                                    onClick={() => {
                                        setTimeout(() => {
                                            myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }, 100);
                                    }}
                                >
                                    <AfitdPagination count={paginationNumber} color="secondary" page={currentPageNumber} onChange={handleChangePage} />
                                </div>
                            )}
                        </SimpleBar>
                    </div>
                </Grid>
                {listModalOpen && <JobSeekersDialog {...defns.listModal} />}
                {showAddModal && <AddJobDialog {...defns.addJobModal} />}
                {editJobModalOpen && <AddJobDialog {...defns.editJobModal} />}
                {hiredModalOpen && <HiredDialog {...defns.hiredModal} />}
                <Grid {...defns.bottomFooter}>{mdMatches ? <Footer /> : null}</Grid>
                <AfitdConfirmDialog {...defns.jobLimitExceedDialog} />
            </Grid>
        </LayoutContext.Provider>
    );
};

export default MyJobs;
